import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import AppShell, { RequireAuth, RequireCapability, Splash } from './AppShell.jsx'
import RouteFallback from './RouteFallback.jsx'
import LoginPage from '../features/auth/LoginPage.jsx'
import TodayPage from '../features/today/TodayPage.jsx'
import { CAP } from '../lib/capabilities.js'
import { useAuth } from '../lib/auth.jsx'

// ─────────────────────────────────────────────────────────────────────────────
// What is eager, and why exactly these three
//
// One bundle for twenty-three screens was 721 kB, and a cashier on a shop's 4G
// connection paid for the reports module, the platform console and the import
// previewer before they could type a customer's name. Every screen below is a
// dynamic import, so the browser fetches a feature the first time somebody
// walks into it.
//
// Three things stay in the entry chunk, because they ARE the first paint and a
// chunk request in front of them would be a white screen on a slow connection:
//
//   LoginPage   what a signed-out browser renders, and the one screen that
//               needs no API behind it
//   AppShell    the top bar and the nav, which must be on screen before the
//               route inside them is even decided
//   TodayPage   where every signed-in session lands. Splitting it would put a
//               second round trip between the password and the dashboard.
//
// Today is no longer the 6 kB card it was when its tiles held placeholders.
// Wiring them to the day book, the ageing report and the sales list brought
// <DataTable> into the entry chunk with it: measured, 87.4 kB gzipped to
// 94.3 kB. Most of that is prepaid rather than added — Billing and Invoices are
// the first two places anybody goes from here, and both render the same table,
// so what the entry chunk gained the first navigation no longer has to fetch.
// It is still the screen behind the password, and it is still not split.
//
// The public landing page is the near miss. It is also what a signed-out
// browser renders — at `/` — but it is a whole marketing page rather than a
// card, and it is the one screen a working shop never sees. So it stays split
// and is prefetched by hand instead, in parallel with the /auth/me call that
// decides whether it is needed at all. See <Root> below.
//
// The Suspense boundary is NOT here. It lives in AppShell around <Outlet />,
// because a boundary wrapped around AppShell would blank the nav and the top
// bar on every navigation — the shell would flicker away and come back on each
// click, which is the exact failure that makes people call an app "web-like".
// Outside the shell there is no chrome to preserve, so those three routes carry
// their own boundary and a card-shaped fallback.
// ─────────────────────────────────────────────────────────────────────────────

// Split like everything else, and prefetched by hand — see <Root> below. It is
// what a signed-out browser renders at `/`, which is the argument for putting
// it in the entry chunk beside LoginPage; but it is a whole marketing page, and
// a cashier who lands on Today twenty times a shift should not carry it. The
// prefetch buys the first half of that trade without paying for the second.
const importLanding = () => import('../features/landing/LandingPage.jsx')
const LandingPage = lazy(importLanding)

const RegisterPage = lazy(() => import('../features/auth/RegisterPage.jsx'))
const VerifyEmailPage = lazy(() => import('../features/auth/VerifyEmailPage.jsx'))
const ForgotPasswordPage = lazy(() => import('../features/admin/ForgotPasswordPage.jsx'))
const ResetPasswordPage = lazy(() => import('../features/admin/ResetPasswordPage.jsx'))
const BillingPage = lazy(() => import('../features/billing/BillingPage.jsx'))
const SaleUploadPage = lazy(() => import('../features/billing/SaleUploadPage.jsx'))
const InvoicesPage = lazy(() => import('../features/invoices/InvoicesPage.jsx'))
const InvoiceDetailPage = lazy(() => import('../features/invoices/InvoiceDetailPage.jsx'))
const OutstandingPage = lazy(() => import('../features/outstanding/OutstandingPage.jsx'))
const ReceivePaymentPage = lazy(() => import('../features/receipts/ReceivePaymentPage.jsx'))
const CustomerPaymentsPage = lazy(() => import('../features/receipts/CustomerPaymentsPage.jsx'))
const CustomersPage = lazy(() => import('../features/parties/CustomersPage.jsx'))
const CustomerDetailPage = lazy(() => import('../features/parties/CustomerDetailPage.jsx'))
const CustomerFormPage = lazy(() => import('../features/parties/CustomerForm.jsx'))
const CustomerOnboardingPage = lazy(() => import('../features/parties/CustomerOnboardingPage.jsx'))
const ProductsPage = lazy(() => import('../features/products/ProductsPage.jsx'))
const ProductFormPage = lazy(() => import('../features/products/ProductForm.jsx'))
const ImportPage = lazy(() => import('../features/imports/ImportPage.jsx'))
const BulkImportPage = lazy(() => import('../features/bulkImports/BulkImportPage.jsx'))
const SuppliersPage = lazy(() => import('../features/parties/SuppliersPage.jsx'))
const SupplierFormPage = lazy(() => import('../features/parties/SupplierForm.jsx'))
const SupplierDetailPage = lazy(() => import('../features/parties/SupplierDetailPage.jsx'))
const SupplierOnboardingPage = lazy(() => import('../features/parties/SupplierOnboardingPage.jsx'))
const ProductRequestsPage = lazy(() => import('../features/product-requests/ProductRequestsPage.jsx'))
const ProductRequestFormPage = lazy(() =>
  import('../features/product-requests/ProductRequestForm.jsx'),
)
const ProductRequestDetailPage = lazy(() =>
  import('../features/product-requests/ProductRequestDetailPage.jsx'),
)
const PurchasesPage = lazy(() => import('../features/purchases/PurchasesPage.jsx'))
const PurchaseFormPage = lazy(() => import('../features/purchases/PurchaseForm.jsx'))
const PurchaseUploadPage = lazy(() => import('../features/purchases/PurchaseUploadPage.jsx'))
const PurchaseDetailPage = lazy(() => import('../features/purchases/PurchaseDetailPage.jsx'))
const PurchasePaymentPage = lazy(() => import('../features/purchases/PurchasePaymentPage.jsx'))
const SupplierPaymentsPage = lazy(() => import('../features/purchases/SupplierPaymentsPage.jsx'))
const SupplierPaymentFormPage = lazy(() =>
  import('../features/purchases/SupplierPaymentForm.jsx'),
)
const ReportsPage = lazy(() => import('../features/reports/ReportsPage.jsx'))
const InsightsPage = lazy(() => import('../features/insights/InsightsPage.jsx'))
const ExpensesPage = lazy(() => import('../features/expenses/ExpensesPage.jsx'))
const ActivityPage = lazy(() => import('../features/reports/ActivityPage.jsx'))
const SettingsPage = lazy(() => import('../features/admin/SettingsPage.jsx'))
const StaffPage = lazy(() => import('../features/admin/StaffPage.jsx'))
const PlatformPage = lazy(() => import('../features/admin/PlatformPage.jsx'))

/** A boundary for the routes that render outside the shell, where there is no
 *  persistent chrome to keep on screen. */
function Unshelled({ children }) {
  return <Suspense fallback={<RouteFallback variant="card" />}>{children}</Suspense>
}

// One <AppShell> layout route wraps <RequireAuth> and <RequireCapability>,
// with capability strings imported from the one shared list that mirrors
// permissions.py (§11a).
//
// The capability on a route is the one the screen's own endpoints require, not
// the one §11a's route map prints. Where the two disagree the server wins: a
// route gated on a capability the API does not check renders a screen that
// 403s on its first request, and the person reading it has no way to tell a
// permission problem from a broken build. Two such corrections, both
// behaviourally identical for every role in the matrix today:
//
//   /suppliers    §11a says `purchases`; every route in suppliers/router.py is
//                 require(MANAGE_PARTIES), reads included.
//   /outstanding  §11a says `sell`; GET /api/reports/outstanding-ageing is
//                 CanViewCustomers. The screen gates taking money on `sell`
//                 in-page instead — hidden, never disabled.
//
// /reports carries no gate on purpose. Its six reports require four different
// capabilities (sell, view_customers, purchases, settings), so the picker
// filters itself, /reports redirects to the first report the caller may read,
// and an unpermitted slug renders a denial panel. One wrapper here would
// either lock a cashier out of the stock report or offer them the GST register.

/**
 * A platform super user has no tenant, so Today has nothing to show them —
 * every tile on it is scoped to a shop. Send them where their one capability
 * actually leads instead of to a dashboard of blanks.
 */
function Home() {
  const { me } = useAuth()
  if (me && !me.tenant) return <Navigate to="/platform/tenants" replace />
  return <TodayPage />
}

// ─────────────────────────────────────────────────────────────────────────────
// `/` is two different pages behind one URL
//
//   signed out          the public landing page, outside the shell
//   signed in           Today, inside the shell
//   platform super user /platform/tenants, because Today is all tenant tiles
//
// and which one it is cannot be known without a round trip, because the session
// cookies are httpOnly and /auth/me is the only way this tab can learn who it
// is. That is the whole problem: any render before the answer arrives is a
// guess, and a guess that turns out wrong is the flash.
//
// So it does not guess. While `status` is 'loading' this renders the same
// <Splash> every other route in the product already renders — the mark and the
// wordmark on the auth ground — and it swaps to the landing page or to Today
// exactly once, when the answer lands. A signed-in cashier on a slow connection
// sees the splash and then Today, and never a word of marketing copy. A visitor
// sees the splash and then the landing page. Neither ever sees the other's
// screen, in either direction.
//
// The alternative was to paint the landing page immediately and redirect
// signed-in users away from it, optionally hinted by a "we have seen a session
// on this browser" flag in localStorage. It is faster for the visitor by one
// round trip and wrong for the shop: the hint is stale after a sign-out
// elsewhere, cleared storage or a second device, and every stale hint is a
// cashier watching the sales page appear on the till. The visitor's round trip
// is one the app makes at boot anyway; the flash would be new.
//
// What IS saved is the second round trip. The landing chunk is fetched in
// parallel with /auth/me rather than after it, so by the time the answer says
// 'anonymous' the code is normally already here and the Suspense fallback below
// never paints. When it does paint it is the same <Splash>, so even that is
// continuous rather than a second loading state.
//
// Deep links are untouched: a signed-out visit to /outstanding still bounces to
// /login carrying `from`, because only `/` has a public page to show.
// ─────────────────────────────────────────────────────────────────────────────
function Root() {
  const { status } = useAuth()
  const { pathname } = useLocation()
  const atRoot = pathname === '/'

  useEffect(() => {
    // Only at `/`, and only while the answer is outstanding — a deep link can
    // never render this page, so fetching it there would be pure cost.
    if (atRoot && status === 'loading') importLanding()
  }, [atRoot, status])

  if (atRoot && status === 'anonymous') {
    return (
      <Suspense fallback={<Splash />}>
        <LandingPage />
      </Suspense>
    )
  }

  // 'loading' and 'offline' both fall through on purpose. RequireAuth already
  // answers them, and its answers are the right ones here too: the splash while
  // we do not know, and the offline notice — never the landing page — when the
  // network failed, because a dropped connection is not a sign-out and the
  // session behind it may be perfectly good.
  return (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  )
}

export const router = createBrowserRouter([
  // Outside the shell, per §09a. /reset must keep this exact path — the mailer
  // sends {base}/reset?token=…
  { path: '/login', element: <LoginPage /> },
  {
    path: '/register',
    element: (
      <Unshelled>
        <RegisterPage />
      </Unshelled>
    ),
  },
  {
    // V014. Must keep this exact path — the verification mail links to
    // {base}/verify-email?token=…
    path: '/verify-email',
    element: (
      <Unshelled>
        <VerifyEmailPage />
      </Unshelled>
    ),
  },
  {
    path: '/forgot',
    element: (
      <Unshelled>
        <ForgotPasswordPage />
      </Unshelled>
    ),
  },
  {
    path: '/reset',
    element: (
      <Unshelled>
        <ResetPasswordPage />
      </Unshelled>
    ),
  },

  {
    // <Root>, not <RequireAuth> directly: `/` is public and everything under it
    // is not. See the note above.
    path: '/',
    element: <Root />,
    children: [
      { index: true, element: <Home /> },
      {
        path: 'platform/tenants',
        element: (
          <RequireCapability capability={CAP.PLATFORM}>
            <PlatformPage />
          </RequireCapability>
        ),
      },
      {
        path: 'billing',
        element: (
          <RequireCapability capability={CAP.SELL}>
            <BillingPage />
          </RequireCapability>
        ),
      },
      {
        // Before nothing dynamic sits under /billing today, but the pattern
        // holds regardless: a literal segment is registered ahead of any
        // future :id route it could otherwise be mistaken for.
        path: 'billing/upload',
        element: (
          <RequireCapability capability={CAP.SELL}>
            <SaleUploadPage />
          </RequireCapability>
        ),
      },
      {
        path: 'invoices',
        element: (
          <RequireCapability capability={CAP.SELL}>
            <InvoicesPage />
          </RequireCapability>
        ),
      },
      {
        path: 'invoices/:id',
        element: (
          <RequireCapability capability={CAP.SELL}>
            <InvoiceDetailPage />
          </RequireCapability>
        ),
      },
      {
        path: 'outstanding',
        element: (
          <RequireCapability capability={CAP.VIEW_CUSTOMERS}>
            <OutstandingPage />
          </RequireCapability>
        ),
      },
      {
        // `sell`, not `view_customers`: this screen POSTs to /api/receipts and
        // /api/receipts/{id}/allocations, both of which are CanSell on the
        // server. Gating it on the weaker capability would render a screen that
        // 403s on its first Save, and the person reading it has no way to tell
        // a permission problem from a broken build (see the note at the top).
        path: 'receipts/new',
        element: (
          <RequireCapability capability={CAP.SELL}>
            <ReceivePaymentPage />
          </RequireCapability>
        ),
      },
      {
        // The list SupplierPaymentsPage already had, mirrored: every receipt
        // taken, on account or against a bill. `sell`, same reasoning as
        // receipts/new above — the day book is a report of every document
        // type at once, and a shop chasing payments specifically wants its
        // own filtered list rather than the whole ledger.
        path: 'receipts',
        element: (
          <RequireCapability capability={CAP.SELL}>
            <CustomerPaymentsPage />
          </RequireCapability>
        ),
      },
      {
        path: 'customers',
        element: (
          <RequireCapability capability={CAP.VIEW_CUSTOMERS}>
            <CustomersPage />
          </RequireCapability>
        ),
      },
      {
        // Before ':id', or "new" is matched as a customer id and the screen
        // asks the server for a uuid that will not parse.
        path: 'customers/new',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <CustomerFormPage />
          </RequireCapability>
        ),
      },
      {
        path: 'customers/:id',
        element: (
          <RequireCapability capability={CAP.VIEW_CUSTOMERS}>
            <CustomerDetailPage />
          </RequireCapability>
        ),
      },
      {
        // Reading a customer needs view_customers; changing one needs
        // manage_parties, so the editor is its own path rather than a mode.
        path: 'customers/:id/edit',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <CustomerFormPage />
          </RequireCapability>
        ),
      },
      {
        // Where a newly-created customer lands (CustomerForm's own onSubmit)
        // before their permanent page — the selling mirror of
        // suppliers/:id/onboarding, two steps rather than three: a first
        // bill and a payment, no catalogue step, because a product belongs
        // to the shop and nothing about selling to someone adds one.
        path: 'customers/:id/onboarding',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <CustomerOnboardingPage />
          </RequireCapability>
        ),
      },
      {
        path: 'products',
        element: (
          <RequireCapability capability={CAP.MANAGE_PRODUCTS}>
            <ProductsPage />
          </RequireCapability>
        ),
      },
      {
        // The three first-day files (V015): suppliers, products with their
        // opening stock, customers. One screen with a tab per file, owner only
        // (bulk_import). A platform super user reaches it by impersonating.
        path: 'imports',
        element: <Navigate to="/imports/suppliers" replace />,
      },
      {
        path: 'imports/:kind',
        element: (
          <RequireCapability capability={CAP.BULK_IMPORT}>
            <BulkImportPage />
          </RequireCapability>
        ),
      },
      {
        // Before ':id', for the same reason 'new' is: "import" would otherwise
        // be matched as a product id and the screen would ask the server for a
        // uuid that will not parse. §11a's route map puts it here, under the
        // product master, because that is what it fills.
        path: 'products/import',
        element: (
          <RequireCapability capability={CAP.MANAGE_PRODUCTS}>
            <ImportPage />
          </RequireCapability>
        ),
      },
      {
        // Before ':id', or "new" is matched as a product id and the screen asks
        // the server for a uuid that will not parse.
        path: 'products/new',
        element: (
          <RequireCapability capability={CAP.MANAGE_PRODUCTS}>
            <ProductFormPage />
          </RequireCapability>
        ),
      },
      {
        path: 'products/:id',
        element: (
          <RequireCapability capability={CAP.MANAGE_PRODUCTS}>
            <ProductFormPage />
          </RequireCapability>
        ),
      },
      {
        path: 'purchases',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <PurchasesPage />
          </RequireCapability>
        ),
      },
      {
        path: 'purchases/new',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <PurchaseFormPage />
          </RequireCapability>
        ),
      },
      {
        // Before ':id', or "upload" is matched as a purchase id and the
        // screen asks the server for a uuid that will not parse.
        path: 'purchases/upload',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <PurchaseUploadPage />
          </RequireCapability>
        ),
      },
      {
        path: 'purchases/:id',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <PurchaseDetailPage />
          </RequireCapability>
        ),
      },
      {
        path: 'purchases/:id/pay',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <PurchasePaymentPage />
          </RequireCapability>
        ),
      },
      {
        path: 'supplier-payments',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <SupplierPaymentsPage />
          </RequireCapability>
        ),
      },
      {
        // A want, not a document (V010) — gated the same as the rest of
        // Buying, since closing one happens only from inside the purchase
        // this bill records.
        path: 'product-requests',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <ProductRequestsPage />
          </RequireCapability>
        ),
      },
      {
        // Before ':id', or "new" is matched as a request id and the screen
        // asks the server for a uuid that will not parse.
        path: 'product-requests/new',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <ProductRequestFormPage />
          </RequireCapability>
        ),
      },
      {
        path: 'product-requests/:id',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <ProductRequestDetailPage />
          </RequireCapability>
        ),
      },
      {
        path: 'supplier-payments/new',
        element: (
          <RequireCapability capability={CAP.PURCHASES}>
            <SupplierPaymentFormPage />
          </RequireCapability>
        ),
      },
      {
        // manage_parties, not purchases — see the note at the top of this file.
        path: 'suppliers',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <SuppliersPage />
          </RequireCapability>
        ),
      },
      {
        path: 'suppliers/new',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <SupplierFormPage />
          </RequireCapability>
        ),
      },
      {
        // Where a newly-created supplier lands (SupplierForm's own onSubmit)
        // before their permanent page: the first bill (new tiles added as it
        // goes, or a price list bulk-imported) and a payment, one screen, none
        // of it forced. Never reached by editing an
        // existing supplier.
        path: 'suppliers/:id/onboarding',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <SupplierOnboardingPage />
          </RequireCapability>
        ),
      },
      {
        // The supplier's own page — ledger, what you can do with them, their
        // details — the same shape as customers/:id.
        path: 'suppliers/:id',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <SupplierDetailPage />
          </RequireCapability>
        ),
      },
      {
        path: 'suppliers/:id/edit',
        element: (
          <RequireCapability capability={CAP.MANAGE_PARTIES}>
            <SupplierFormPage />
          </RequireCapability>
        ),
      },
      {
        // The owner's charts. view_cost, which is what GET
        // /api/reports/insights requires: the page shows what the shop paid
        // suppliers and what its slow stock cost.
        path: 'insights',
        element: (
          <RequireCapability capability={CAP.VIEW_COST}>
            <InsightsPage />
          </RequireCapability>
        ),
      },
      {
        // Running costs (V020). Every tenant role records them; a cashier sees
        // only their own lines, which the server decides.
        path: 'expenses',
        element: (
          <RequireCapability capability={CAP.RECORD_EXPENSES}>
            <ExpensesPage />
          </RequireCapability>
        ),
      },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'reports/:report', element: <ReportsPage /> },
      {
        path: 'settings',
        element: (
          <RequireCapability capability={CAP.SETTINGS}>
            <SettingsPage />
          </RequireCapability>
        ),
      },
      {
        path: 'settings/staff',
        element: (
          <RequireCapability capability={CAP.MANAGE_STAFF}>
            <StaffPage />
          </RequireCapability>
        ),
      },
      {
        path: 'settings/activity',
        element: (
          <RequireCapability capability={CAP.SETTINGS}>
            <ActivityPage />
          </RequireCapability>
        ),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
